import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const WHAT_WE_BUILD = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18M8 15h8" />
      </svg>
    ),
    title: "Guard Scheduling & Rostering",
    desc: "Drag-and-drop scheduling across posts and sites with overtime alerts, certification checks, and shift-swap requests. Fill open shifts in minutes, not hours.",
    color: "border-blue-500/30 bg-blue-500/5",
    accent: "text-blue-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    ),
    title: "GPS Patrol & Tour Tracking",
    desc: "QR/NFC checkpoint scanning and live GPS breadcrumbs prove guards walked their tours. Missed-checkpoint alerts notify supervisors in real time.",
    color: "border-indigo-500/30 bg-indigo-500/5",
    accent: "text-indigo-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" />
      </svg>
    ),
    title: "Incident Reporting",
    desc: "Guards file incident reports with photos, timestamps, and GPS from the field. Structured logs, daily activity reports, and instant supervisor escalation.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
      </svg>
    ),
    title: "Time & Attendance",
    desc: "Geofenced clock-in/out with selfie verification stops buddy-punching. Automatic timesheets flow straight into billing and payroll.",
    color: "border-blue-500/30 bg-blue-500/5",
    accent: "text-blue-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3 3h18v4H3zM3 10h18v4H3zM3 17h18v4H3zM7 5h.01M7 12h.01M7 19h.01" />
      </svg>
    ),
    title: "Billing & Payroll",
    desc: "Bill-rate vs. pay-rate logic, per-site invoicing, and overtime rules automated. Export timesheets to QuickBooks, ADP, or Gusto in a click.",
    color: "border-indigo-500/30 bg-indigo-500/5",
    accent: "text-indigo-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M3 9h18M7 14h4" />
      </svg>
    ),
    title: "Client Portal & Dashboards",
    desc: "Give clients a branded portal to view live guard activity, tour logs, and incident reports — proof of service that wins renewals and referrals.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
];

const TECH = [
  "React", "React Native", "Node.js", "PostgreSQL", "PostGIS", "Google Maps API",
  "QR / NFC", "Twilio", "AWS", "WebSockets", "Push Notifications", "QuickBooks / ADP",
];

const PROCESS = [
  {
    step: "01",
    title: "Operations Discovery",
    desc: "We map your sites, posts, guard licensing rules, and billing structure to design a system that matches how you run patrols and shifts.",
  },
  {
    step: "02",
    title: "Workflow & UX Design",
    desc: "Scheduler boards, guard app flows, and client portals prototyped in Figma and validated with your supervisors before build.",
  },
  {
    step: "03",
    title: "Agile Development",
    desc: "Two-week sprints with deployed previews. Scheduling, patrol tracking, incident reporting, and billing shipped incrementally.",
  },
  {
    step: "04",
    title: "Integrations & Rollout",
    desc: "Payroll, accounting, and telephony wired in. Checkpoint hardware (QR/NFC) provisioned and tested across your sites.",
  },
  {
    step: "05",
    title: "Launch & Support",
    desc: "Data migration, supervisor and guard training, phased rollout — followed by monitoring, SLAs, and ongoing iteration.",
  },
];

const FEATURES = [
  "Drag-and-drop shift scheduling",
  "Open-shift & swap management",
  "Overtime & fatigue alerts",
  "License & certification tracking",
  "QR / NFC checkpoint tours",
  "Live GPS guard tracking",
  "Missed-checkpoint alerts",
  "Incident reports with photo & GPS",
  "Geofenced time & attendance",
  "Automated billing & payroll export",
  "Branded client proof-of-service portal",
  "Lone-worker & panic button safety",
];

export default function SecurityGuardDetail() {
  useSEO({
    title: "Security Guard Company Software | Patrol & Scheduling | BerryByte",
    description: "Custom software for security guard companies — guard scheduling, GPS patrol & tour tracking, incident reporting, time & attendance, billing and a client portal.",
    keywords: "security guard company software, guard scheduling software, security patrol tracking software, guard tour system, incident reporting software, security workforce management, guard time and attendance",
    canonical: "https://www.berrybyteinn.com/services/security-guard-solutions",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-blue-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-indigo-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / Security Guard Solutions
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              Scheduling · Patrol Tracking · Billing
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Security Software<br />
              <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent">That Guards Your Margins.</span>
            </h1>

            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              An all-in-one platform for security guard companies. Schedule guards, prove patrols with GPS and checkpoints, capture incidents in the field, and turn verified hours into accurate billing and payroll.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-base shadow-2xl shadow-blue-900/40 hover:shadow-blue-900/60 hover:-translate-y-1 transition-all duration-300">
                Book a Demo
                <span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "100%", label: "Verified Patrols" }, { value: "0", label: "Buddy-Punching" }, { value: "1-Click", label: "Payroll Export" }, { value: "24/7", label: "Live Oversight" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-blue-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-blue-400 font-semibold text-sm tracking-widest uppercase">What We Build</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">From the schedule to the site,<br /><span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">every shift accounted for.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WE_BUILD.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className={`rounded-2xl border ${item.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${item.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-base font-bold mb-2 ${item.accent}`}>{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase">How We Work</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Built around your posts.<br /><span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">No guesswork.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((step) => (
              <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="text-xs font-bold text-blue-500 tracking-widest mb-2">{step.step}</div>
                <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-blue-400 font-semibold text-sm tracking-widest uppercase">Technology</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Our security stack.</h2>
              <div className="flex flex-wrap gap-3">
                {TECH.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-blue-500/20 bg-blue-500/5 text-blue-300 text-sm font-medium hover:bg-blue-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Everything a guard company needs.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-blue-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
            Run a security company?<br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Let's tighten it up.</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">
            Book a free 30-minute discovery call. We'll review your scheduling, patrol, and billing workflow and show you where software can save hours and win contracts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow-2xl shadow-blue-900/40 hover:shadow-blue-900/60 hover:-translate-y-1 transition-all duration-300">
              Get a Free Consultation<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
